import { defineStore } from 'pinia';
import { ref } from 'vue';
import api from '../services/api';

export interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  data?: Record<string, unknown>;
  readAt: string | null;
  createdAt: string;
  sender?: {
    id: number;
    name: string;
    avatar: string | null;
  } | null;
}

export interface ToastNotification {
  id: number;
  title: string;
  message: string;
  type: string;
  sender?: { name: string; avatar: string | null } | null;
}

export const useNotificationsStore = defineStore('notifications', () => {
  const notifications = ref<Notification[]>([]);
  const loading = ref(false);
  const hasMore = ref(true);
  const page = ref(1);
  const perPage = 20;
  const toasts = ref<ToastNotification[]>([]);
  const unreadCount = ref(0);
  /** Bumped by $reset (sign-in / sign-out): a page from before is dropped. */
  let generation = 0;

  async function fetchNotifications(reset = false): Promise<void> {
    if (reset) {
      page.value = 1;
      hasMore.value = true;
    }

    if (!hasMore.value || loading.value) return;

    loading.value = true;
    const gen = generation;
    try {
      const response = await api.get('/notifications', {
        params: { page: page.value, limit: perPage },
      });
      if (gen !== generation) return;

      const { data, pagination, unreadCount: serverUnread } = response.data;

      if (reset) {
        notifications.value = data;
      } else {
        notifications.value.push(...data);
      }

      unreadCount.value = Number(serverUnread) || 0;
      hasMore.value = page.value < pagination.pages;
      page.value++;
    } finally {
      if (gen === generation) loading.value = false;
    }
  }

  async function markAsRead(id: number): Promise<void> {
    await api.patch(`/notifications/${id}/read`);
    const notification = notifications.value.find(n => n.id === id);
    if (notification && !notification.readAt) {
      notification.readAt = new Date().toISOString();
      unreadCount.value = Math.max(0, unreadCount.value - 1);
    }
  }

  async function markAllAsRead(): Promise<void> {
    await api.patch('/notifications/read-all');
    notifications.value.forEach(n => {
      if (!n.readAt) {
        n.readAt = new Date().toISOString();
      }
    });
    unreadCount.value = 0;
  }

  async function deleteNotification(id: number): Promise<void> {
    await api.delete(`/notifications/${id}`);
    const notification = notifications.value.find(n => n.id === id);
    if (notification && !notification.readAt) {
      unreadCount.value = Math.max(0, unreadCount.value - 1);
    }
    notifications.value = notifications.value.filter(n => n.id !== id);
  }

  function addNotification(notification: Notification): void {
    notifications.value.unshift(notification);
    if (!notification.readAt) {
      unreadCount.value++;
    }
    // Show toast
    const toast: ToastNotification = {
      id: notification.id,
      title: notification.title,
      message: notification.message,
      type: notification.type,
      sender: notification.sender,
    };
    toasts.value.push(toast);
    setTimeout(() => {
      toasts.value = toasts.value.filter(t => t.id !== toast.id);
    }, 5000);
  }

  function dismissToast(id: number): void {
    toasts.value = toasts.value.filter(t => t.id !== id);
  }

  /** Forget the signed-in user's notifications (stores/sessionScope.ts). */
  function $reset(): void {
    generation++;
    notifications.value = [];
    loading.value = false;
    hasMore.value = true;
    page.value = 1;
    toasts.value = [];
    unreadCount.value = 0;
  }

  return {
    notifications,
    loading,
    hasMore,
    unreadCount,
    toasts,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    addNotification,
    dismissToast,
    $reset,
  };
});
